import * as z from "@-/com.npmjs.zod";
import { CHINA_MAP_REGION_IDS } from "@-/dev.improve.haniverse.data.china-map";

const point = z.tuple([z.number(), z.number()]);
const bbox = z.tuple([z.number(), z.number(), z.number(), z.number()]);
const regionId = z.enum(CHINA_MAP_REGION_IDS);
const place = z.object({
  adcode: z.number().int().positive(),
  bbox,
  category: z.enum([
    "autonomous-prefecture",
    "autonomous-region",
    "city",
    "county",
    "district",
    "forestry-district",
    "league",
    "municipality",
    "parish",
    "planning-area",
    "prefecture",
    "province",
    "region",
    "special-administrative-region",
    "town",
    "township",
  ]),
  centroid: point,
  coast: z.array(bbox).optional(),
  englishName: z.string().optional(),
  labels: z
    .array(
      z.object({
        anchor: point,
        em: z.number().positive(),
        isScattered: z.boolean().optional(),
        offsets: z.array(point),
      }),
    )
    .min(1),
  lonLat: point,
  name: z.string(),
  path: z.string(),
  pinyin: z.array(z.string().regex(/^[a-züv:]+[0-4]$/)),
  shortName: z.string(),
  traditionalShortName: z.string(),
});

/** Versioned wire format. Validate network and disk bytes before rendering or saving them. */
export const mapDownloadSchema = z.object({
  map: z.object({
    height: z.number().positive(),
    provinces: z.array(place.extend({ children: z.array(place) })).min(1),
    width: z.number().positive(),
  }),
  regionId,
  schemaVersion: z.literal(1),
  // Small global index: names, distances and "Show current location" work across map tabs
  // without downloading every other region's geometry.
  places: z.array(
    z.object({
      adcode: z.number().int().positive(),
      bbox,
      lonLat: point,
      regionId,
      shortName: z.string(),
      traditionalShortName: z.string(),
    }),
  ),
  transport: z.object({
    nodes: z.array(
      z.object({
        airports: z.array(z.string()),
        hsr: z.boolean(),
        hubKey: z.number(),
        isProvinceHub: z.boolean(),
        lonLat: point,
        provinceAdcode: z.number(),
        rail: z.boolean(),
        shortName: z.string(),
        zone: z.enum(["hainan", "mainland"]),
      }),
    ),
    places: z.array(
      z.object({
        adcode: z.number(),
        hubKey: z.number(),
        lonLat: point,
        metro: z.enum(["beijing", "shanghai", "tianjin"]).nullable(),
        provinceAdcode: z.number(),
      }),
    ),
    routes: z.array(
      z.object({
        from: z.number(),
        options: z.array(
          z.object({
            distanceKm: z.number(),
            durationMinutes: z.number(),
            example: z.string(),
            fareCny: z.number(),
            mode: z.enum(["flight", "hsr", "rail"]),
            seatClass: z.enum(["economy", "hard-seat", "hard-sleeper", "second"]),
            serviceCount: z.number(),
            sourceUrl: z.string(),
          }),
        ),
        to: z.number(),
      }),
    ),
    sources: z.object({
      flightFareDate: z.string().nullable(),
      flightsRetrievedAt: z.string().nullable(),
      railRetrievedAt: z.string().nullable(),
    }),
  }),
});

export type MapDownload = z.infer<typeof mapDownloadSchema>;
